<template>
  <section
    class="w-full flex flex-row justify-center testimmonial-outer-container bg-themeComponentBg-light font-rubik"
    aria-labelledby="testimonial-heading"
  >
    <h2 id="testimonial-heading" class="sr-only">Customer testimonials</h2>

    <div :class="`w-full ${aboutUs ? ' max-w-1216' : ' max-w-1024'}`">
      <div
        :class="`w-full flex-col flex 1240-bp:flex-row bg-testimonialBackground testimmonial-container ${aboutUs ? 'border 1240-bp:rounded-md' : ''}`"
      >
        <div class="w-full 1240-bp:w-5/12 desktop-left-side-container">
          <div class="h-full flex flex-col items-start justify-end gap-1">
            <h3 class="testimonial-h1">Testimonials</h3>
            <p class="text-black testimonial-p">
              Don't take our word for it. Trust our customers.
            </p>
          </div>
        </div>

        <div class="w-7/12 hidden 1240-bp:grid desktop-right-side-container">
          <div
            :class="`grid grid-cols-12 blurred-div ${
              aboutUs ? 'border 1240-bp:rounded-md' : ''
            }`"
            role="list"
            aria-label="Customer testimonials (desktop)"
          >
            <div class="col-span-10 flex flex-row">
              <div
                ref="leftCol"
                class="w-full flex flex-col items-end overflow-scroll card-column-container"
              >
                <div
                  v-for="(testimonial, index) in leftCol"
                  :key="`left-${index}`"
                  role="listitem"
                >
                  <testimonial-card :testimonial="testimonial" />
                </div>
              </div>

              <div
                ref="rightCol"
                class="w-full flex flex-col items-start overflow-scroll card-column-container"
              >
                <div class="shadow-right">
                  <div
                    v-for="(testimonial, index) in rightCol"
                    :key="`right-${index}`"
                    role="listitem"
                  >
                    <testimonial-card :testimonial="testimonial" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="w-full 1240-bp:hidden pl-5 pt-5 pb-5 testimonial-mobile-card-main-container"
        >
          <div
            class="testimonial-mobile-card-wrapper"
            role="list"
            aria-label="Customer testimonials (mobile)"
          >
            <div
              class="w-full flex flex-row overflow-x-auto gap-4 testimonial-mobile-card-container"
            >
              <testimonial-card-mobile
                v-for="(testimonial, index) in testimonials"
                :key="`testimonial-${index}`"
                :testimonial="testimonial"
                role="listitem"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import TestimonialCard from './helpers/testimonialCard.vue'
import TestimonialCardMobile from './helpers/testimonialCardMobile.vue'

export default {
  components: {
    TestimonialCard,
    TestimonialCardMobile
  },
  props: {
    testimonials: {
      type: Array,
      required: true
    },
    aboutUs: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      leftCol: [],
      rightCol: [],
      scrollSpeedFactor: 0.05 // scroll speed (0.5 means half the speed of the main scroll)
    }
  },
  mounted() {    
    this.divideArray()
    this.initializeScrollPositions()
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    divideArray() {
      const midIndex = Math.ceil(this.testimonials.length / 2)
      this.leftCol = this.testimonials.slice(0, midIndex)
      this.rightCol = this.testimonials.slice(midIndex)
    },
    initializeScrollPositions() {
      this.$refs.leftCol.scrollTop = 0 // Start leftCol at the top
      this.$refs.rightCol.scrollTop = this.$refs.rightCol.scrollHeight - this.$refs.rightCol.clientHeight // Start rightCol at the bottom
    },
    handleScroll() {
      const scrollPosition = window.scrollY * this.scrollSpeedFactor
      const leftScrollTop = scrollPosition
      const rightScrollTop = (this.$refs.rightCol.scrollHeight - this.$refs.rightCol.clientHeight) - scrollPosition

      this.$refs.leftCol.scrollTop = leftScrollTop
      this.$refs.rightCol.scrollTop = rightScrollTop
    }
  }
}
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.testimmonial-outer-container {
  padding-top: 112px;
}

.testimonial-container {
  height: 695px;
}

.card-column-container {
  height: 695px;
    &::-webkit-scrollbar {
    width: 0px;
  }
}

.testimonial-mobile-card-main-container {
  position: relative;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    width: 0px;
  }
}

.testimonial-mobile-card-wrapper {
  position: relative;
  overflow: hidden;
}

.testimonial-mobile-card-container {
  display: flex;
  overflow-x: auto;
  &::-webkit-scrollbar {
    width: 0px;
  }
}

/* Mobile card: Apply shadow effect to the right side */
.testimonial-mobile-card-wrapper::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 60px;
  height: 100%;
  background: linear-gradient(to left, rgba(238, 236, 233, 1), rgba(238, 236, 233, 0.7) 50%, rgba(255, 255, 255, 0));
  pointer-events: none;
  z-index: 1;
}

.desktop-left-side-container {
  padding: 45px;
}

.desktop-right-side-container {
  max-height: 677px;
}

.shadow-right {
  box-shadow: 5px 0px 25px rgba(0, 0, 0, 0.3);
}

.blurred-div {
  position: relative;
  box-shadow: 0 20px 20px -20px rgba(0, 0, 0, 0.3) inset,
              0 -20px 20px -20px rgba(0, 0, 0, 0.3) inset;
  overflow: hidden;
}

/* Desktop: Adding gradient effect to blur */
.blurred-div::before,
.blurred-div::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 40px;
  z-index: 2;
  pointer-events: none;
}

.blurred-div::before {
  top: 0;
  background: linear-gradient(to bottom, rgba(238, 236, 233, 0.8), rgba(255, 255, 255, 0));
}

.blurred-div::after {
  bottom: 0;
  background: linear-gradient(to top, rgba(238, 236, 233, 0.8), rgba(255, 255, 255, 0));
}

.testimonial-h1 {
  font-size: 32px;
  font-weight: 400;
  line-height: 38.4px;
  letter-spacing: -0.04em;
  color: black;
}

.testimonial-p {
  font-size: 18px;
  font-weight: 400;
  line-height: 27px;
}

/* Mobile */
@media(max-width: 1240px) {
  .testimmonial-outer-container {
    padding-top: 20px;
  }

  .testimonial-container {
    height: 351px;
  }

  .testimonial-h1 {
    font-size: 24px;
    font-weight: 400;
    line-height: 33.6px;
    letter-spacing: -0.04em;
  }

  .testimonial-p {
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
  }

  .desktop-left-side-container {
    padding: 20px;
  }
}
</style>
